import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, KeyRound, CheckCircle2, X, Lock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { user, login, error } = useAuth();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // Forgot / Reset Password Modal state
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);
  const [resetStep, setResetStep] = useState("request"); // "request", "new_password", "success"
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [forgotError, setForgotError] = useState("");

  if (user && user !== false) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    try {
      await login("goldennuggets.admin@gmail.com", password);
    } finally {
      setBusy(false);
    }
  };

  const handleRequestResetToken = async () => {
    setForgotBusy(true);
    setForgotError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email: "goldennuggets.admin@gmail.com" });
      if (data?.reset_token) {
        setResetToken(data.reset_token);
        setResetStep("new_password");
      } else {
        setResetStep("success");
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err) || "Unable to process password reset request. Please check connection.";
      setForgotError(msg);
    } finally {
      setForgotBusy(false);
    }
  };

  const handleExecuteResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      setForgotError("Password must be at least 6 characters long.");
      return;
    }
    setForgotBusy(true);
    setForgotError("");
    try {
      await api.post("/auth/reset-password", { token: resetToken, new_password: newPassword });
      setResetStep("success");
    } catch (err) {
      const msg = formatApiErrorDetail(err) || "Failed to update password. Token may be expired.";
      setForgotError(msg);
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="dark grid min-h-screen place-items-center bg-background px-4 text-foreground">
      <div className="w-full max-w-md">
        {/* Brand Header */}
        <div className="mb-8 flex items-center justify-center gap-3">
          <div className="grid h-12 w-12 place-items-center rounded-2xl bg-primary/15 ring-1 ring-primary/30">
            <span className="font-serif text-2xl text-primary">G</span>
          </div>
          <div>
            <div className="font-serif text-xl text-foreground">Golden Nuggets</div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              Content Control
            </div>
          </div>
        </div>

        {/* Single-Password Login Form */}
        <form
          data-testid="login-form"
          onSubmit={onSubmit}
          className="space-y-5 rounded-2xl border hairline bg-card p-6 lg:p-8 shadow-glow"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Sign in</p>
            <h1 className="mt-2 font-serif text-2xl text-foreground">Welcome back</h1>
            <p className="mt-1 text-sm text-muted-foreground">Administrator access only.</p>
          </div>

          <label className="block">
            <div className="mb-1.5 text-[13px] text-foreground font-medium">Password</div>
            <div className="relative">
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                required
                autoFocus
                className="h-11 w-full rounded-lg border hairline bg-background/40 px-3.5 pr-10 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <KeyRound className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/50" />
            </div>
          </label>

          {error && (
            <div data-testid="login-error" className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive-foreground">
              {typeof error === "string" ? error : formatApiErrorDetail(error)}
            </div>
          )}

          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={busy || !password}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground shadow-glow transition hover:opacity-95 disabled:opacity-50"
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Connecting…
              </>
            ) : (
              "Sign in"
            )}
          </button>

          <div className="pt-2 text-center">
            <button
              type="button"
              onClick={() => {
                setShowForgotModal(true);
                setResetStep("request");
                setResetToken("");
                setNewPassword("");
                setForgotError("");
              }}
              className="text-xs text-muted-foreground hover:text-primary transition hover:underline"
            >
              Forgot Password?
            </button>
          </div>
        </form>
      </div>

      {/* Forgot / Reset Password Direct Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-background/80 px-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border hairline bg-card p-6 shadow-glow">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-xl text-foreground">Reset Password</h2>
              <button
                onClick={() => setShowForgotModal(false)}
                className="p-1 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {resetStep === "request" && (
              <div className="mt-4 space-y-4">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Reset password for official lead administrator account:
                </p>

                <div className="rounded-xl border hairline bg-background/50 p-3 text-center text-sm font-medium text-primary font-mono">
                  goldennuggets.admin@gmail.com
                </div>

                {forgotError && (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive-foreground">
                    {forgotError}
                  </div>
                )}

                <div className="mt-6 flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowForgotModal(false)}
                    className="rounded-full border hairline bg-surface-2/40 px-4 py-2 text-sm text-foreground hover:bg-surface-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={forgotBusy}
                    onClick={handleRequestResetToken}
                    className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground shadow-glow disabled:opacity-70"
                  >
                    {forgotBusy ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Generating Reset Session…
                      </>
                    ) : (
                      "Reset Admin Password"
                    )}
                  </button>
                </div>
              </div>
            )}

            {resetStep === "new_password" && (
              <form onSubmit={handleExecuteResetPassword} className="mt-4 space-y-4">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Set a new password for <strong className="text-foreground font-mono">goldennuggets.admin@gmail.com</strong>:
                </p>

                <label className="block">
                  <div className="mb-1.5 text-[13px] text-foreground font-medium">New Password</div>
                  <div className="relative">
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      required
                      minLength={6}
                      autoFocus
                      className="h-11 w-full rounded-lg border hairline bg-background/40 px-3.5 pr-10 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <Lock className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/50" />
                  </div>
                </label>

                {forgotError && (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive-foreground">
                    {forgotError}
                  </div>
                )}

                <div className="mt-6 flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowForgotModal(false)}
                    className="rounded-full border hairline bg-surface-2/40 px-4 py-2 text-sm text-foreground hover:bg-surface-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={forgotBusy || !newPassword}
                    className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground shadow-glow disabled:opacity-70"
                  >
                    {forgotBusy ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Updating Password…
                      </>
                    ) : (
                      "Save & Sign In"
                    )}
                  </button>
                </div>
              </form>
            )}

            {resetStep === "success" && (
              <div className="mt-4 space-y-4 text-center">
                <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-primary/15 text-primary">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <h3 className="font-serif text-lg text-foreground">Password Updated Successfully!</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Your administrator password has been updated in PostgreSQL.<br />
                  You may now sign in with your new password.
                </p>
                <div className="pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotModal(false);
                      if (newPassword) setPassword(newPassword);
                    }}
                    className="rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground shadow-glow"
                  >
                    Back to Sign In
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
